import { Col, Input, Popconfirm, Row, Table, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import { ReactComponent as DeleteOutlined } from "@images/circle_xmark.svg";
import { deleteReportUser, getAllReportUsers } from "@/ts/utils";
import { debounce } from "underscore";
import { GrEdit } from "react-icons/gr";
import AddOrEditReportUsers from "./AddOrEditReportUsers";
import { IReportUserRecord, IUserSettingModalTable } from "@/ts/common-types";
import { showToast } from "@/ts/modals";
import CustomDrawer from "../CustomDrawer";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "@/ts/constants";

const UserSettingsModalTable = ({
    updateRecords,
    setUpdateRecords,
}: IUserSettingModalTable) => {
    const [paginationData, setPaginationData] = useState({
        usersList: new Map<number, IReportUserRecord[]>(),
        currentPage: 1,
        pageSize: 5,
        totalUsers: 0,
        loading: false,
    });

    const [searchQuery, setSearchQuery] = useState("");
    const [editUserDisplay, setEditUserDisplay] = useState(false);
    const [recordToEdit, setRecordToEdit] = useState<IReportUserRecord>({
        id: null,
        username: "",
    });
    const [deleteloading, setDeleteLoading] = useState(false);

    const fetchUsers = async (page: number, pageSize: number) => {
        setPaginationData((prev) => ({
            ...prev,
            loading: true,
        }));

        try {
            const res = await getAllReportUsers({
                page,
                pageSize,
                searchQuery: searchQuery,
            });

            const usersData = res?.usersData;
            const totalUsers = res?.totalUsers;

            if (!usersData) {
                throw new Error("Invalid response structure");
            }

            setPaginationData((prev) => ({
                ...prev,
                usersList: new Map(prev.usersList).set(page, usersData),
                currentPage: page,
                totalUsers: totalUsers,
                loading: false,
            }));
        } catch (error) {
            setPaginationData((prev) => ({
                ...prev,
                loading: false,
            }));
            showToast("danger", "Error fetching users");
        }
    };

    useEffect(() => {
        const { currentPage, pageSize } = paginationData;

        const pageToFetch = currentPage > 1 ? currentPage : 1;

        fetchUsers(pageToFetch, pageSize);
    }, [
        searchQuery,
        paginationData.currentPage,
        paginationData.pageSize,
        updateRecords,
    ]);

    const updatePaginationAndSearch = (value: string) => {
        setPaginationData((prev) => ({
            ...prev,
            currentPage: 1,
            usersList: new Map(),
            loading: true,
        }));
        setSearchQuery(value);
    };

    const debouncedSearch = debounce((value: string) => {
        if (!value || value.length > 3) {
            updatePaginationAndSearch(value);
        }
    }, 500);

    const columns = [
        {
            title: "User Name",
            dataIndex: "username",
            key: "username",
            render: (text: string, record: any) => <>{record.username || ""}</>,
        },
        {
            title: "Actions",
            dataIndex: "actions",
            key: "actions",
            render: (text: string, record: any) => (
                <>
                    <div className="d-flex align-items-center gap-4">
                        <div>
                            <Tooltip placement="left" title="Edit User">
                                <GrEdit
                                    style={{
                                        fontSize: "18px",
                                        cursor: "pointer",
                                    }}
                                    onClick={() => {
                                        setRecordToEdit(record);
                                        setEditUserDisplay(true);
                                    }}
                                />
                            </Tooltip>
                        </div>

                        <div>
                            <Tooltip placement="right" title="Delete User ">
                                <Popconfirm
                                    title="Are you sure you want to delete this user?"
                                    onConfirm={async () => {
                                        try {
                                            setDeleteLoading(true);
                                            const response =
                                                await deleteReportUser({
                                                    id: record.id,
                                                });

                                            if (response?.status) {
                                                showToast(
                                                    "success",
                                                    response.message
                                                );
                                                setDeleteLoading(false);
                                                setUpdateRecords(
                                                    (prev) => !prev
                                                );

                                                setPaginationData((prev) => ({
                                                    ...prev,
                                                    usersList: new Map<
                                                        number,
                                                        any
                                                    >(),
                                                    currentPage: 1,
                                                    loading: true,
                                                }));
                                            }
                                        } catch (err) {
                                            setDeleteLoading(false);
                                            showToast(
                                                "danger",
                                                "Error deleting user"
                                            );
                                        }
                                    }}
                                    okText="Yes"
                                    cancelText="No"
                                    okButtonProps={{ loading: deleteloading }}
                                    styles={{
                                        body: { padding: "10px" },
                                    }}
                                >
                                    <DeleteOutlined
                                        role="button"
                                        height={20}
                                        width={20}
                                        fill="#d9534f"
                                    />
                                </Popconfirm>
                            </Tooltip>
                        </div>
                    </div>
                </>
            ),
        },
    ];
    return (
        <div>
            <div className="mt-4">
                <Row>
                    <Col span={8}>
                        {paginationData.loading ? (
                            ""
                        ) : (
                            <h5>
                                All Users{" "}
                                <span className="text-muted">
                                    ({paginationData.totalUsers || 0})
                                </span>
                            </h5>
                        )}
                    </Col>
                    <Col span={16} className="text-end">
                        <Input
                            placeholder="Search by user name"
                            className="border w-50"
                            defaultValue={searchQuery}
                            onChange={(e) => {
                                const value = e.target.value.trim();
                                debouncedSearch(value);
                            }}
                            allowClear
                        />
                    </Col>
                </Row>

                <Table
                    className="mt-2"
                    rowKey={(record) => record.id}
                    dataSource={
                        paginationData.usersList.get(
                            paginationData.currentPage
                        ) || []
                    }
                    columns={columns}
                    pagination={{
                        current: paginationData.currentPage,
                        pageSize: paginationData.pageSize,
                        total: paginationData.totalUsers,
                        onChange: (page) => {
                            setPaginationData((prev) => ({
                                ...prev,
                                currentPage: page,
                            }));
                        },
                        onShowSizeChange: (current, size) => {
                            setPaginationData((prev) => ({
                                usersList: new Map(),
                                currentPage: 1,
                                pageSize: size,
                                totalUsers: prev.totalUsers,
                                loading: true,
                            }));
                            // scrollToTable()
                        },
                        showSizeChanger: true,
                        pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
                    }}
                    loading={paginationData.loading}
                    scroll={{ y: 390 }}
                />
            </div>
            <CustomDrawer
                title="Edit User"
                width={500}
                open={editUserDisplay}
                onClose={() => setEditUserDisplay(false)}
                footer={false}
            >
                <AddOrEditReportUsers
                    editUserDisplay={editUserDisplay}
                    setEditUserDisplay={setEditUserDisplay}
                    setUpdateRecords={setUpdateRecords}
                    isEdit={true}
                    record={recordToEdit}
                />
            </CustomDrawer>
        </div>
    );
};

export default UserSettingsModalTable;
